/**
 * Zero-role shell gate — defense-in-depth behind Entra "Assignment required".
 *
 * The worker already refuses every API call for an authenticated session
 * without app roles (403 "No permissions assigned"), so no data was ever
 * exposed — but the SPA still rendered its full shell (sidebar-less layout,
 * dashboard scaffolding, background fetch attempts) around those 403s. This
 * gate sits directly inside PermissionProvider: until roles resolve nothing
 * renders but a spinner, a session with zero roles gets ONLY the no-access
 * screen (no routes, no feature code, no entitlement fetches), and a
 * transient /api/users/me failure gets an explicit retry screen instead of
 * being misreported as "no access" (fail loudly, never mask).
 *
 * Impersonation sessions are unaffected: they carry Global.Admin roles.
 */

import { useMsal } from '@azure/msal-react'
import { Button, Card, CardBody, Spinner } from '../ui/primitives'
import { usePermissions } from '../context/PermissionContext'
import { clearSessionBootstrap } from '../services/apiClient'
import { resetSessionQueries } from '../query/queryClient'

function CenteredShell({ children, testId }) {
  return (
    <div
      className="d-flex align-items-center justify-content-center min-vh-100"
      data-testid={testId}
    >
      {children}
    </div>
  )
}

export default function AccessGate({ children }) {
  const { loading, loadFailed, appRoles, refreshPermissions } = usePermissions()
  const { instance, accounts } = useMsal()
  const accountEmail = accounts?.[0]?.username || ''

  const signOut = () => {
    // An impersonation token would re-authenticate the session on reload;
    // drop it before handing sign-out to MSAL.
    clearSessionBootstrap()
    resetSessionQueries()
    window.sessionStorage.removeItem('impersonationToken')
    instance.logoutRedirect().catch(() => {
      window.location.reload()
    })
  }

  if (loading) {
    return (
      <CenteredShell testId="access-gate-loading">
        <Spinner />
      </CenteredShell>
    )
  }

  if (loadFailed) {
    return (
      <CenteredShell testId="access-gate-error">
        <Card style={{ maxWidth: 440 }}>
          <CardBody className="text-center">
            <h4>Couldn&apos;t load your permissions</h4>
            <p className="text-medium-emphasis">
              The dashboard could not verify your access. This is usually
              transient — retry, and contact your administrator if it persists.
            </p>
            <div className="d-flex gap-2 justify-content-center">
              <Button color="primary" onClick={() => refreshPermissions()} data-testid="access-gate-retry">
                Retry
              </Button>
              <Button color="secondary" onClick={signOut}>
                Sign out
              </Button>
            </div>
          </CardBody>
        </Card>
      </CenteredShell>
    )
  }

  if (appRoles.length === 0) {
    return (
      <CenteredShell testId="access-gate-denied">
        <Card style={{ maxWidth: 440 }}>
          <CardBody className="text-center">
            <h4>No access</h4>
            <p className="text-medium-emphasis">
              {accountEmail ? <><strong>{accountEmail}</strong> is signed in, but this</> : 'This'}{' '}
              account has no permissions assigned for this application. Ask your
              administrator to assign an app role in Microsoft Entra ID.
            </p>
            <Button color="primary" onClick={signOut} data-testid="access-gate-signout">
              Sign out
            </Button>
          </CardBody>
        </Card>
      </CenteredShell>
    )
  }

  return children
}
